/**
 * Owner: Masa Bayu
 * File: PrivateAuthorMobileHomeParts.tsx
 * Date:Time: 2026-10-23 09:20
 * Description: Kad halaman utama mudah alih PRIVAT §467-C — Prestasi Bulanan
 *   (pil bulan + jualan/pesanan + carta garis daripada `monthly` hub), Pintasan
 *   Pantas (sasaran laluan nyata `AUTHOR_SHORTCUTS`), karusel Buku Saya (katalog
 *   milik akaun) dan Notifikasi (HANYA fakta pelayan yang menunggu tindakan).
 *   Dibuang: siri jualan karangan, roalti tanpa sumber, 4 notifikasi demo +
 *   masa rekaan, lencana DEMO dan katalog pasaran penuh yang disalah-pam sebagai
 *   "buku saya".
 */

import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { LandingIcon } from '../../home/marketplace/landing/LandingIcons';
import { qxCategoryName } from '@qx/qxread-core';
import {
  formatMarketplacePriceOrUnknown,
  resolveMarketplaceLabels,
} from '../../home/marketplace/marketplace-locale';
import type { MarketplaceLocaleProfile } from '../../home/marketplace/marketplace-locale';
import { ALERT_ACTIONS, AUTHOR_SHORTCUTS, alertText } from './private-author-view';
import type { AuthorDashboardModel, AuthorSeriesPoint } from './private-author-view';
import type { PrivateAuthorLabels, PrivateShortcutId } from './private-author-labels';
import type { PrivateAuthorMobileLabels } from './private-author-mobile-labels';

const CHART_W = 340;
const CHART_H = 150;
const PAD_X = 14;
const PAD_TOP = 12;
const PAD_BOTTOM = 24;

/** Titik kosong untuk kes susut indeks — tidak pernah dipaparkan (siri bukan null). */
const NO_POINT: AuthorSeriesPoint = { month: '', label: '', minorUnits: 0, orders: 0 };

const TONES = ['#dbeafe', '#fde8e8', '#e7f6ef', '#fef3c7', '#f3e8ff'];

function Stroke({
  children,
  size = 18,
}: {
  readonly children: ReactNode;
  readonly size?: number;
}): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

const SHORTCUT_ICONS: Record<PrivateShortcutId, ReactElement> = {
  upload: (
    <Stroke>
      <path d="M12 16V4m0 0l-4 4m4-4l4 4" />
      <path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
    </Stroke>
  ),
  bookPlus: (
    <Stroke>
      <path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 1-2-2z" />
      <path d="M12 7v6M9 10h6" />
    </Stroke>
  ),
  megaphone: (
    <Stroke>
      <path d="M4 11l12-6v14L4 13zM4 11v3M18 8a3 3 0 0 1 0 6" />
    </Stroke>
  ),
  report: (
    <Stroke>
      <path d="M5 21V9l5-5 9 3v13z" />
      <path d="M9 13h6M9 17h6" />
    </Stroke>
  ),
  price: (
    <Stroke>
      <path d="M12 3v6" />
      <circle cx="12" cy="15" r="6" />
      <path d="M9.8 13.4c1.5-1.2 3.8-.6 4.4.8.7 1.6-1 2.4-2.2 2.4-1.3 0-2.4.8-2.2 2.1.2 1.2 1.9 1.7 3.4 1" />
    </Stroke>
  ),
  receipt: (
    <Stroke>
      <path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" />
      <path d="M9 8h6M9 12h6" />
    </Stroke>
  ),
};

export interface MobilePrestasiProps {
  readonly la: PrivateAuthorLabels;
  readonly ml: PrivateAuthorMobileLabels;
  readonly model: AuthorDashboardModel;
}

export function MobilePrestasi({ la, ml, model }: MobilePrestasiProps): ReactElement {
  const [pick, setPick] = useState<number | null>(null);
  const series = model.series;
  if (series.length === 0) {
    return (
      <section className="qxpm__card" aria-label={ml.prestasiTitle}>
        <h2 className="qxpm__card-title">{ml.prestasiTitle}</h2>
        <p className="qxpm__empty">{la.chartEmpty}</p>
      </section>
    );
  }

  // Belum dipilih = bulan terakhir (bulanan semasa), bukan bulan terlama.
  const idx = Math.min(Math.max(pick ?? series.length - 1, 0), series.length - 1);
  const point = series[idx] ?? NO_POINT;
  const previous = idx > 0 ? series[idx - 1] : undefined;
  const deltaPct =
    previous !== undefined && previous.minorUnits > 0
      ? Math.round(((point.minorUnits - previous.minorUnits) / previous.minorUnits) * 100)
      : null;
  const priceOf = (minorUnits: number): string =>
    formatMarketplacePriceOrUnknown(minorUnits, model.currency, la.priceUnknown);

  const max = Math.max(...series.map((p) => p.minorUnits), 1);
  const step = series.length > 1 ? (CHART_W - PAD_X * 2) / (series.length - 1) : 0;
  const xOf = (i: number): number => (series.length > 1 ? PAD_X + step * i : CHART_W / 2);
  const yOf = (v: number): number =>
    CHART_H - PAD_BOTTOM - (v / max) * (CHART_H - PAD_TOP - PAD_BOTTOM);
  const points = series.map((p, i) => ({ x: xOf(i), y: yOf(p.minorUnits), p }));
  const line = points
    .map((pt, i) => `${i === 0 ? 'M' : 'L'}${String(pt.x)},${String(pt.y)}`)
    .join(' ');
  const first = points[0];
  const lastPt = points[points.length - 1];
  const area =
    first === undefined || lastPt === undefined
      ? ''
      : `${line} L${String(lastPt.x)},${String(CHART_H - PAD_BOTTOM)} L${String(first.x)},${String(
          CHART_H - PAD_BOTTOM,
        )} Z`;
  const tipX = Math.min(Math.max(xOf(idx) - 62, 2), CHART_W - 126);
  const tipY = Math.max(yOf(point.minorUnits) - 34, 2);

  return (
    <section className="qxpm__card" aria-label={ml.prestasiTitle}>
      <div className="qxpm__card-head">
        <h2 className="qxpm__card-title">{ml.prestasiTitle}</h2>
      </div>
      <label className="qxpm__month">
        <span className="qxpm__sr">{ml.prestasiMonthAria}</span>
        <select
          value={idx}
          onChange={(event) => {
            setPick(Number(event.target.value));
          }}
        >
          {series.map((p, i) => (
            <option key={p.month} value={i}>
              {`${p.label} ${p.month.slice(0, 4)}`}
            </option>
          ))}
        </select>
      </label>
      <div className="qxpm__metrics">
        <p className="qxpm__metric">
          <em>{ml.salesLabel}</em>
          <strong>{priceOf(point.minorUnits)}</strong>
          {deltaPct === null ? null : (
            <span
              className={
                deltaPct >= 0 ? 'qxpm__delta qxpm__delta--up' : 'qxpm__delta qxpm__delta--down'
              }
            >
              {deltaPct >= 0 ? '↑' : '↓'} {String(Math.abs(deltaPct))}%
            </span>
          )}
        </p>
        <p className="qxpm__metric">
          <em>{ml.ordersLabel}</em>
          <strong>{String(point.orders)}</strong>
        </p>
      </div>
      <svg
        className="qxpm__line"
        viewBox={`0 0 ${String(CHART_W)} ${String(CHART_H)}`}
        role="img"
        aria-label={la.chartSalesAria}
      >
        <defs>
          <linearGradient id="qxpmArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgba(47,128,255,.24)" />
            <stop offset="100%" stopColor="rgba(47,128,255,0)" />
          </linearGradient>
        </defs>
        {points.map((pt) => (
          <line
            key={`g${pt.p.month}`}
            className="qxpm__grid"
            x1={pt.x}
            y1={PAD_TOP}
            x2={pt.x}
            y2={CHART_H - PAD_BOTTOM}
          />
        ))}
        <path className="qxpm__line-area" d={area} fill="url(#qxpmArea)" />
        <path className="qxpm__line-path" d={line} />
        {points.map((pt, i) => (
          <g
            key={pt.p.month}
            onClick={() => {
              setPick(i);
            }}
          >
            <rect
              x={pt.x - step / 2}
              y={0}
              width={Math.max(step, 1)}
              height={CHART_H}
              fill="transparent"
            />
            <circle
              className={`qxpm__dot${i === idx ? ' qxpm__dot--on' : ''}`}
              cx={pt.x}
              cy={pt.y}
              r={i === idx ? 5 : 3}
            />
            <text className="qxpm__x" x={pt.x} y={CHART_H - 6} textAnchor="middle">
              {pt.p.label}
            </text>
          </g>
        ))}
        <g className="qxpm__tip">
          <rect x={tipX} y={tipY} width="124" height="24" rx="6" />
          <text x={tipX + 62} y={tipY + 16} textAnchor="middle">
            {`${point.label} ${point.month.slice(0, 4)} · ${priceOf(point.minorUnits)}`}
          </text>
        </g>
      </svg>
    </section>
  );
}

export interface MobileShortcutsProps {
  readonly onNavigate: (path: string) => void;
  readonly la: PrivateAuthorLabels;
  readonly ml: PrivateAuthorMobileLabels;
}

export function MobileShortcuts({ onNavigate, la, ml }: MobileShortcutsProps): ReactElement {
  return (
    <section className="qxpm__card" aria-label={ml.shortcutsTitle}>
      <h2 className="qxpm__card-title">{ml.shortcutsTitle}</h2>
      <div className="qxpm__shortcuts">
        {AUTHOR_SHORTCUTS.map((s) => (
          <button
            key={s.id}
            type="button"
            className="qxpm__shortcut qx-motion-press"
            onClick={() => {
              onNavigate(s.target);
            }}
          >
            <span className="qxpm__shortcut-ic" aria-hidden="true">
              {SHORTCUT_ICONS[s.id]}
            </span>
            {la.shortcutLabels[s.id]}
          </button>
        ))}
      </div>
    </section>
  );
}

export interface MobileBooksRailProps {
  readonly onNavigate: (path: string) => void;
  readonly openView: (view: 'buku') => void;
  readonly profile: MarketplaceLocaleProfile;
  readonly la: PrivateAuthorLabels;
  readonly ml: PrivateAuthorMobileLabels;
  readonly model: AuthorDashboardModel;
}

export function MobileBooksRail({
  onNavigate,
  openView,
  profile,
  la,
  ml,
  model,
}: MobileBooksRailProps): ReactElement {
  const t = resolveMarketplaceLabels(profile);
  return (
    <section className="qxpm__card" aria-label={ml.railTitle}>
      <div className="qxpm__card-head">
        <h2 className="qxpm__card-title">{ml.railTitle}</h2>
        <button
          type="button"
          className="qxpm__link"
          onClick={() => {
            openView('buku');
          }}
        >
          {ml.railSeeAll} <LandingIcon name="chevronRight" size={12} />
        </button>
      </div>
      {model.books.length === 0 ? (
        <p className="qxpm__empty">{la.booksEmpty}</p>
      ) : (
        <ul className="qxpm__rail" role="list" aria-label={ml.railTitle}>
          {model.books.map((book) => (
            <li key={book.publicationId}>
              <button
                type="button"
                className="qxpm__rcard"
                onClick={() => {
                  onNavigate(`/publication/${encodeURIComponent(book.publicationId)}`);
                }}
              >
                <span
                  className="qxpm__rcover"
                  style={{ background: TONES[book.coverTone % TONES.length] ?? '#dbeafe' }}
                >
                  <span>{book.title}</span>
                  <em className={`qxpm__rchip${book.listed ? '' : ' qxpm__rchip--off'}`}>
                    {book.listed ? la.status.published : la.status.unlisted}
                  </em>
                </span>
                <strong className="qxpm__rtitle">{book.title}</strong>
                {book.category === null ? null : (
                  <em className="qxpm__rgenre">{qxCategoryName(book.category, t.language)}</em>
                )}
                <span className="qxpm__rmeta">
                  <strong>{book.priceText}</strong>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export interface MobileNotificationsProps {
  readonly onNavigate: (path: string) => void;
  readonly la: PrivateAuthorLabels;
  readonly ml: PrivateAuthorMobileLabels;
  readonly model: AuthorDashboardModel;
}

export function MobileNotifications({
  onNavigate,
  la,
  ml,
  model,
}: MobileNotificationsProps): ReactElement {
  return (
    <section className="qxpm__card" id="qxpm-notifikasi" aria-label={ml.notifTitle}>
      <div className="qxpm__card-head">
        <h2 className="qxpm__card-title">{ml.notifTitle}</h2>
      </div>
      {model.alerts.length === 0 ? (
        <p className="qxpm__empty">{la.notificationsEmpty}</p>
      ) : (
        <ul className="qxpm__notifs">
          {model.alerts.map((alert) => (
            <li key={alert.id}>
              <button
                type="button"
                className="qxpm__notif"
                onClick={() => {
                  onNavigate(ALERT_ACTIONS[alert.kind].target);
                }}
              >
                <span className="qxpm__notif-ic" aria-hidden="true">
                  <LandingIcon name={ALERT_ACTIONS[alert.kind].icon} size={16} />
                </span>
                <span className="qxpm__notif-text">{alertText(alert, la)}</span>
                <LandingIcon name="chevronRight" size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
